import { z } from 'zod';
import { ITEM_LEVEL_CAPS } from '../inventory/definitions';
import { CANNON_RARITIES } from '../rarity';

/**
 * What a duelling ship LOOKS like, decided by the server and by nothing else.
 *
 * ## Why appearance is authoritative at all
 *
 * A client that drew its opponent from its own idea of their equipment would be
 * guessing, and the guess is worth something: a ship's art says which hull it
 * is and its cannons say how it shoots. A player who could choose what the
 * other screen shows could show a Common hull while flying a Prismatic one, and
 * the opponent would misread every engagement. So the profile is resolved from
 * the owner's authoritative loadout, signed into their socket ticket, and
 * echoed to both seats by the room. Nothing a client sends can change it.
 *
 * ## Frozen for the duel, exactly as the hunt freezes its loadout
 *
 * The profile is read once, when the ticket is minted, and the room keeps the
 * copy it was given. Equipping a different cannon in another tab does not
 * reshape a ship mid-duel, and a reconnecting player comes back looking like
 * what they came in as. This mirrors `hunt_sessions.passives`: what a run was
 * started with is a property of the run.
 *
 * ## And it is versioned
 *
 * {@link PVP_APPEARANCE_VERSION} is bumped whenever the MEANING of a field
 * changes - a new field a client must understand to draw correctly, or a
 * re-interpretation of one that exists. A client that receives a version above
 * the one it was built for draws the fallback rather than something wrong, and
 * a room restored from storage across a deploy is re-read through the same
 * check. Adding art, or a new cannon rarity, is not a version change: those are
 * values in fields that already mean what they say.
 *
 * Version 2 replaced a barrel COUNT with the cannon LEVEL. The two are equal
 * today, which is exactly why it was worth changing before anything drew from
 * it: a field that is right by coincidence is a field that breaks silently.
 *
 * Version 3 added {@link pvpAppearanceSchema.shape.skin}. A client built for
 * version 2 has no idea a hull could wear anything, so it would draw the
 * ordinary ship for a player who is wearing a skin - which is a picture two
 * screens would disagree about, and disagreeing about what a ship looks like is
 * the one thing this file exists to prevent.
 */
export const PVP_APPEARANCE_VERSION = 3;

/**
 * The characters a skin's `definitionId` may use on this wire.
 *
 * ## Why the charset is part of the contract and not a lint
 *
 * Because the signed socket ticket encodes the whole profile as ONE
 * hyphen-separated field inside a dot-separated payload (`pvp/ticket.ts`,
 * `pvp/team-ticket.ts`), and both parsers demand an exact part count so that no
 * value can shift the meaning of the fields after it. Every component was a
 * digit run or a rarity word until now; a definition id is the first component
 * that is an arbitrary string, and one containing `-` or `.` would split into
 * two parts and change what every later field means.
 *
 * The catalogue's own id schema is `z.string().min(3).max(64)` and deliberately
 * stays that way - it describes content, not transport. This is the narrower
 * thing the WIRE accepts, so an id that could break a ticket is refused at the
 * boundary rather than trusted to never be authored.
 */
export const PVP_SKIN_ID_PATTERN = /^[a-z0-9_]{3,64}$/;

/**
 * The highest cannon level, and therefore the widest wing ever drawn.
 *
 * Read from the item catalogue rather than written again, so a duel cannot
 * outlive a change to how far a cannon levels. It doubles as the bound on how
 * many sprites a malformed or hostile profile can ask a scene to build, since
 * level `L` mounts exactly `L` barrels.
 */
export const PVP_MAX_CANNON_LEVEL = ITEM_LEVEL_CAPS.cannon ?? 1;

export const pvpAppearanceSchema = z
  .object({
    /** {@link PVP_APPEARANCE_VERSION} as it stood when this profile was made. */
    v: z.number().int().min(1),
    /** Which hull, as the ship catalogue numbers them. One-based, like the catalogue. */
    ship: z.number().int().min(1),
    /**
     * The equipped cannon's rarity, or `null` for a ship with no cannon.
     *
     * `null` is a real state and not an error: a brand new account has a hull
     * and no gun, and it is allowed into a duel. The scene draws the hull
     * alone.
     */
    cannon: z.enum(CANNON_RARITIES).nullable(),
    /**
     * The cannon's LEVEL, which is what decides where the barrels sit.
     *
     * This field used to be a barrel COUNT, and the difference is not
     * cosmetic. The geometry function the hunt draws from - `cannonFormation`
     * in `game-domain` - takes a level, and a renderer handed a count would
     * have to invert one into the other. That inversion happens to be the
     * identity today, because level `L` mounts exactly `L` barrels, and the
     * day it stops being the identity the wing would silently change shape
     * with nothing failing. So the profile carries what the geometry actually
     * consumes; the count is derived from the formation, never assumed.
     *
     * Zero means no cannon, which pairs with a `null` rarity.
     */
    level: z.number().int().min(0).max(PVP_MAX_CANNON_LEVEL),
    /**
     * The skin worn on this hull, as a CONTENT identity, or `null`.
     *
     * ## The minimum a client needs, and deliberately nothing more
     *
     * A `definitionId`, which the receiving build looks up in its own validated
     * skin manifest. That is the whole field. It is not an `itemInstanceId`,
     * because two copies of a skin are two economic objects and one picture and
     * letting the copy choose the art would invent a difference the content
     * does not have. It is not an asset key, a file name or a descriptor,
     * because a client that drew what the sender named would be drawing
     * whatever reached it - and in a game with ownership, what reaches it is
     * influenced by data a player can affect. It carries no socket, no jewel,
     * no attribute, no seed, no price and no ledger, because a duel draws a
     * ship and none of those are a ship.
     *
     * ## Unknown is not an error
     *
     * An id this build cannot resolve - absent from the manifest, `draft`
     * outside the harness, or with a PNG that fails to load - draws the
     * ordinary hull. Never a refused match and never a hole where a ship
     * should be: a duel one player cannot see is worse than a duel drawn
     * plainly.
     */
    skin: z.string().regex(PVP_SKIN_ID_PATTERN).nullable(),
  })
  .strict()
  /*
   * A rarity and a level agree, or the profile is not a ship.
   *
   * Either both say there is a cannon or both say there is not. A rarity with
   * level zero would draw no barrels for a gun the player owns, and a level
   * with no rarity would ask for barrels with no art to draw them from.
   */
  .refine(
    (profile) => (profile.cannon === null) === (profile.level === 0),
    'a cannon rarity and a cannon level must both be present or both be absent',
  );

export type PvpAppearance = z.infer<typeof pvpAppearanceSchema>;

/**
 * What a seat looks like when its real profile could not be read.
 *
 * A duel must never fail to draw. If the loadout query is unavailable at ticket
 * time, or a stored profile comes back from a version this build does not
 * understand, the seat gets the first hull and no cannon - which is a real
 * appearance an account can legitimately have, so nothing downstream needs a
 * special case for it.
 */
export const PVP_FALLBACK_APPEARANCE: PvpAppearance = {
  v: PVP_APPEARANCE_VERSION,
  ship: 1,
  cannon: null,
  level: 0,
  // Undressed, for the same reason the cannon is absent: the fallback is the
  // appearance of an account that owns nothing, which is a real one.
  skin: null,
};

/**
 * A skin id fit to travel, or `null`.
 *
 * ## Why this exists rather than trusting the catalogue
 *
 * Because of what an unfit id would COST. The profile is one object: if a
 * definition id outside {@link PVP_SKIN_ID_PATTERN} were written into it, the
 * whole profile would fail to parse at the far end and the seat would fall back
 * to {@link PVP_FALLBACK_APPEARANCE} - losing the player's HULL and CANNON as
 * well, over a name. Dropping the skin alone is the honest degradation: the
 * ship somebody equipped is still the ship they are drawn in, and the only
 * thing missing is the decoration that could not be named.
 *
 * Called on the way OUT, where a definition id first meets the wire.
 */
export function pvpWireSkinId(definitionId: string | null | undefined): string | null {
  if (!definitionId || !PVP_SKIN_ID_PATTERN.test(definitionId)) {
    return null;
  }
  return definitionId;
}

/**
 * Read a profile this build knows how to draw, or the fallback.
 *
 * The version gate is here rather than at each call site so that a room
 * restored from storage, a welcome arriving over a socket and a ticket being
 * verified all answer the question the same way.
 */
export function pvpReadAppearance(value: unknown): PvpAppearance {
  const parsed = pvpAppearanceSchema.safeParse(value);
  if (!parsed.success || parsed.data.v > PVP_APPEARANCE_VERSION) {
    return PVP_FALLBACK_APPEARANCE;
  }
  return parsed.data;
}

/**
 * The three things a duel can pick up, named where the WIRE can reach them.
 *
 * The list lives in `shared` rather than in the domain's balance file for one
 * structural reason: `shared` must not depend on `game-domain`, and the socket
 * schema has to validate the name. The domain re-exports it, so there is still
 * exactly one list and a fourth rune is one edit.
 *
 * ## There was a fourth, and it is gone from the game
 *
 * The retired rune is not named here, and that is the point: the list IS the
 * vocabulary. Three things follow from it at once, none of which needs its own
 * guard:
 *
 * - the draw is `PVP_RUNE_KINDS[rng(0, length - 1)]`, so no match can grow one;
 * - `pvpRuneFrameSchema.k` is `z.enum` over this list, so no frame can name one
 *   and no client can ask for one;
 * - `PvpRuneKind` does not include it, so `PVP_RUNE_DURATION_TICKS` and
 *   `pvpApplyRune` cannot describe or apply it and the compiler says so.
 *
 * The decision is [ADR 0038](../../../../docs/adr/0038-mirages-are-removed.md).
 */
export const PVP_RUNE_WIRE_KINDS = ['double_damage', 'speed', 'teleport'] as const;

export type PvpRuneWireKind = (typeof PVP_RUNE_WIRE_KINDS)[number];

/*
 * `PVP_RETIRED_RUNE_KIND` was here, and it was the last place the removed
 * mechanic's NAME existed in code (gate V28H.H.1).
 *
 * It held the string so two reading paths could turn an old pad into an empty
 * one instead of refusing the document. That tolerance is gone with it: a
 * document naming the removed rune is now refused like any other unknown kind,
 * which is what "the mechanic does not exist" actually means.
 *
 * The tolerance protected a case that cannot occur. It only ever applied to a
 * room evicted mid-match, PvP has never been deployed, and `PVP_SNAPSHOT_V3_VERSION`
 * has moved seventeen times since - so the only documents that could carry the
 * name are ones no build in existence has ever written outside a test.
 *
 * And it was not free: the constant is a string literal, so it survived
 * minification and shipped in the bundle a player downloads. Two lockdown
 * guards named it, and both were red for exactly that reason.
 */

/**
 * **Quantos caracteres de um nickname atravessam o fio** (gate V28H.R).
 *
 * Uma etiqueta por cima de uma nave, e não uma frase: vinte cabem no espaço que
 * a barra de vida já ocupa, e um nome mais longo é aparado em vez de recusado -
 * uma conta com um nome grande continua a poder duelar.
 *
 * O limite também é uma defesa: sem ele, um nome de dez mil caracteres seria um
 * cliente a tapar a arena de toda a gente com texto.
 */
export const PVP_NICKNAME_MAX = 20;

/**
 * O nome público de quem se sentou, como o fio o leva.
 *
 * `min(1)`: uma cadeira sem nome diz-se com `null`, e não com uma string vazia.
 * Duas maneiras de dizer a mesma ausência seriam duas maneiras de a esquecer.
 */
export const pvpNicknameSchema = z.string().min(1).max(PVP_NICKNAME_MAX);

/** Apara um nome ao que cabe, preservando o que lá está. */
export function pvpNickname(displayName: string): string | null {
  const trimmed = displayName.trim().slice(0, PVP_NICKNAME_MAX);
  return trimmed === '' ? null : trimmed;
}
